import { getMarket } from '@atc/bonnet-reference';
// Fetchers
import {
    fetchDealerLot,
    fetchDealerLotSingle,
    fetchPreviouslyViewedInventory,
    fetchModels,
    fetchYears,
    fetchMakes,
} from '@/fetchers';

// Ducks
import {
    inventoryDuck,
    dealerLotResultsDuck,
    dealerLotDuck,
    inventoryResultsDuck,
    ownersDuck,
} from '@/ducks';

import {
    makeDropDownMask,
    modelDropDownMask,
    yearDropDownMask,
    getInventoryWithOwner,
} from '@/utilities/dataTransformers';

import { setUserZipCookie } from '@/utilities';

// multi-dealer elot
export function getDealerLot(query, mockQueries) {
    return async (dispatch) => {
        fetchDealerLot(query, mockQueries)
            .then((response) => {
                const inventory = response.listings || [];
                const activeResults = inventory.map((listing) => listing.id);
                const owners = inventory.map((listing) => listing.owner);

                [
                    inventoryDuck.creators.addInventory(inventory),
                    dealerLotResultsDuck.creators.setActiveResults(activeResults),
                    ownersDuck.creators.addOwners(owners),
                    dealerLotDuck.creators.setKeys({ fetchedDealerLot: true }),
                ].forEach((action) => {
                    dispatch(action);
                });
            });
    };
}

// Reformats query to include zip & use correct key names
export function formatQuery(query, zip) {
    const reformattedQuery = {
        ...query,
        ...(query.vehicleStyleCodes && { bodyStyleCode: query.vehicleStyleCodes }),
        ...(query.listingTypes && { listingType: query.listingTypes.toUpperCase() }),
        ...(query.makeCodeList && { makeCode: query.makeCodeList }),
        ...(query.modelCodeList && { modelCode: query.modelCodeList }),
        ...(query.trimCodeList && { trimCode: query.trimCodeList }),
        ...(query.seriesCodeList && { seriesCode: query.seriesCodeList }),
        zip,
    };

    delete reformattedQuery.vehicleStyleCodes;
    delete reformattedQuery.listingTypes;
    delete reformattedQuery.makeCodeList;
    delete reformattedQuery.modelCodeList;
    delete reformattedQuery.seriesCodeList;
    delete reformattedQuery.trimCodeList;
    return reformattedQuery;
}

// updates the query to use the proper data depending on whats available
export function updateDealerLot(recentSearches, consumerPreferences, pageContext, zip, modelCodeToBoost, mockQueries) {
    let query = { zip };

    const { topRankedVehicle } = consumerPreferences;
    const hasConsumerPreferences = topRankedVehicle && topRankedVehicle.make;

    if (Array.isArray(modelCodeToBoost) && modelCodeToBoost.length > 0) {
        query = formatQuery({ modelCodeToBoost }, zip);
    } else if (recentSearches) {
        query = formatQuery(recentSearches, zip);
    } else if (hasConsumerPreferences) {
        const consumerPreferencesQuery = {
            makeCode: topRankedVehicle.make && topRankedVehicle.make.code,
            modelCode: topRankedVehicle.model && topRankedVehicle.model.code,
            bodyStyleCode: topRankedVehicle.bodyStyle && topRankedVehicle.bodyStyle.code,
        };
        query = formatQuery(consumerPreferencesQuery, zip);
    } else if (Object.keys(pageContext).length > 0) {
        query = formatQuery(pageContext, zip);
    }

    return (dispatch) => {
        dispatch(getDealerLot(query, mockQueries));
    };
}

export function getPreviouslyViewedInventory(items) {
    return (dispatch) => {
        if (items.length > 0) {
            fetchPreviouslyViewedInventory(items).then((response) => {
                if (response) {
                    const inventory = response.listings || [];
                    const activeResults = items.map((listing) => listing.id);
                    const owners = inventory.map((listing) => listing.owner);

                    [
                        inventoryDuck.creators.addInventory(inventory),
                        inventoryResultsDuck.creators.setActiveResults(activeResults),
                        inventoryResultsDuck.creators.setCount(activeResults.length),
                        ownersDuck.creators.addOwners(owners),
                    ].forEach((action) => {
                        dispatch(action);
                    });
                }
            });
        }
    };
}

export async function getMakesByListingType(listingType) {
    return fetchMakes({ listingType })
        .then((makes) => makeDropDownMask(makes));
}

export async function getModelsByMakeDropdown(makeCode) {
    return fetchModels({ makeCode })
        .then((models) => modelDropDownMask(makeCode, models));
}

export async function getYearsByModelDropDown(event, modelCode) {
    return fetchYears({ modelCode })
        .then((years) => yearDropDownMask(years));
}

export async function validateZip(zip) {
    const { success, error = null } = await getMarket(zip);
    // if zip is legit, set ATC_USER_ZIP cookie so it can persist across apps
    if (success) {
        setUserZipCookie(zip);
    }

    // Ex: [BONNET REFERENCE] [getMarket] cannot find market data for zip: 11111, city: undefined, state: undefined
    // In the case of an error do not block the user from continuing search to the SRP
    if (error) {
        throw new Error(error);
    }

    return success; // if success is false, the catch block is invoked
}

// single-dealer elot
export function getDealerLotSingle(query, mockQueries) {
    return async (dispatch) => {
        fetchDealerLotSingle(query, mockQueries)
            .then((response) => {
                const listings = response.listings || [];
                const owner = response.owner || {};
                const inventory = getInventoryWithOwner({ listings, owner });
                const activeResults = inventory.map((listing) => listing.id);

                [
                    dealerLotDuck.creators.setKeys({ ...response, eLotQueryReady: true, fetchedDealerLot: true, activeResults }),
                    // dealerLotResultsDuck.creators.setActiveResults(activeResults),
                    inventoryDuck.creators.addInventory(inventory),
                    ownersDuck.creators.addOwners([owner]),
                ].forEach((action) => {
                    dispatch(action);
                });
            });
    };
}

export default {
    getPreviouslyViewedInventory,
    updateDealerLot,
    getModelsByMakeDropdown,
    getYearsByModelDropDown,
    getDealerLotSingle,
    validateZip,
};
